"use client";

import React from "react";
import { Pagination } from "fansunited-sports-ui";
import { cn } from "@/utilities/core/cn";

type PaginationProps = React.ComponentProps<typeof Pagination>;
type PaginationStyles = NonNullable<PaginationProps["styles"]>;

// Buttons inherit the tenant's configured card surface (border, radius, shadow,
// hover) by reusing the project's `bg-card` utility from tailwind.css. The
// active page is highlighted with the primary theme tokens. Attribute selectors
// give the active rule higher specificity than the inherited `bg-card` so its
// background and text colors win without touching the card's border/radius.
const CONTAINER_CLASSES = [
    "[&_button]:bg-card",
    "[&_button]:text-card-foreground",
    "[&_button[aria-current=page]]:bg-primary",
    "[&_button[aria-current=page]]:text-primary-foreground",
    "[&_button[aria-current=page]:hover]:bg-primary",
    "[&_button[aria-current=page]:hover]:text-primary-foreground",
].join(" ");

/**
 * Pagination styled to inherit the tenant's configured card appearance.
 *
 * Wraps the upstream `Pagination` from `fansunited-sports-ui` and applies a
 * container className that pushes `bg-card` onto every button via descendant
 * arbitrary variants. All borders, radii, shadows, and hover states come from
 * the tenant's `--card-*` CSS variables — no hardcoded geometry.
 *
 * Caller `styles` (gap, additional container classes, typography overrides) are
 * preserved and merged via twMerge.
 */
export function CardPagination(props: PaginationProps) {
    const callerStyles = (props as { styles?: PaginationStyles }).styles;

    const styles: PaginationStyles = {
        ...callerStyles,
        container: cn(CONTAINER_CLASSES, callerStyles?.container),
        typography: callerStyles?.typography,
    };

    return <Pagination {...(props as PaginationProps)} styles={styles} />;
}
